import { sortBy } from 'lodash'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { getI18n } from 'react-i18next'
import agent from '../../api/agent'
import { sortByType } from '../../common/sortHelper'
import { IProjects } from '../../interfaces/projects/IProjects'
import {
  ICategoryTag,
  IEditedSkillInfo,
  ILinkedProject,
  INewSkill,
  ISkill,
  ISkillDepObject,
  ISkillForm,
  ISkillProject,
  ISkills,
} from '../../interfaces/skills/ISkills'
import { ISortDirection } from '../../interfaces/Sort'

export default class SkillStore {
  sortColumn = 'skillName'
  sortDirection: ISortDirection = 'ascending'
  categoryFilter: number | null = null
  parentFilter: number | null = null

  /**
   * * Loader observables
   */

  loadingRecentSkills = false
  loadingInitial = false
  loadingSkills = false
  loadingLikeButton = false

  /**
   * * Skill observables
   */

  isNewPersonSkill = false
  allSkills: ISkills[] = []
  recentSkills: ISkills[] = []
  currentSkill: any = {}
  currentProject: any = {}
  selectedSkillLevel: number | null = null
  allUserProjectIds: IProjects[] = []
  editCurrentSkill: ISkillForm = {
    approved: false,
    comment: '',
    experience: 0,
    interest: 0,
    linkedProjects: [],
    skillId: 0,
    skillLevel: 0,
    skillNameFi: '',
    skillNameEng: '',
    skillDescription: '',
    skillDescriptionEng: '',
    parentSkillId: 0,
  }
  editCurrentProject: Array<ILinkedProject> = []
  skills: ISkill[] = []
  loadingNeededSkills = false
  userNeededSkills: ISkillDepObject[] = []
  loadingAllNeededSkills = false
  allUserNeededSkills: ISkills[] = []
  allDepartments: string[] = []
  sortedNeededSkills: ISkillDepObject[] = []
  userDepartment = ''

  /**
   * * Tag and category observables
   */
  tags: Array<ICategoryTag> = []
  selectedEditSkill?: ISkill
  loadingTags = false
  categories: Array<ICategoryTag> = []
  loadingCategories = false

  /**
   * * Modal and show/hide observables
   */

  saving = false
  deleting = false
  showModal = false
  showSkill = false
  showNewSkillModal = false
  showEditSkillModal = false
  showProjectTable = false
  showCreateNewSkillModal = false
  showAddProjectToSkillModal = false
  showEditInfo = false
  showUserSkillEdit = true

  /**
   * * Search observables
   */
  isSearching = false
  searchValue = ''

  constructor() {
    makeObservable(this, {
      sortColumn: observable,
      sortDirection: observable,
      categoryFilter: observable,
      parentFilter: observable,
      loadingRecentSkills: observable,
      loadingInitial: observable,
      loadingSkills: observable,
      loadingLikeButton: observable,
      isNewPersonSkill: observable,
      allSkills: observable,
      recentSkills: observable,
      currentSkill: observable,
      currentProject: observable,
      selectedSkillLevel: observable,
      allUserProjectIds: observable,
      editCurrentSkill: observable,
      editCurrentProject: observable,
      skills: observable,
      loadingNeededSkills: observable,
      userNeededSkills: observable,
      loadingAllNeededSkills: observable,
      allUserNeededSkills: observable,
      allDepartments: observable,
      sortedNeededSkills: observable,
      userDepartment: observable,
      tags: observable,
      selectedEditSkill: observable,
      loadingTags: observable,
      categories: observable,
      loadingCategories: observable,
      saving: observable,
      deleting: observable,
      showModal: observable,
      showSkill: observable,
      showNewSkillModal: observable,
      showEditSkillModal: observable,
      showProjectTable: observable,
      showCreateNewSkillModal: observable,
      showAddProjectToSkillModal: observable,
      showEditInfo: observable,
      showUserSkillEdit: observable,
      isSearching: observable,
      searchValue: observable,
      dropdownTags: computed,
      filteredSkills: computed,
      dropdownCategories: computed,
      dropdownSkills: computed,
      allDropdownSkills: computed,
      allParentSkills: computed,
      getFilteredSkillsByText: action,
      setParentFilter: action,
      setShowAddProjectToSkillModal: action,
      setCategoryFilter: action,
      setCurrentskill: action,
      setSaving: action,
      setDeleting: action,
      setSearchValue: action,
      setSortColumn: action,
      toggleSortDirection: action,
      setAllSkills: action,
      clearEditCurrent: action,
      setEditCurrentProject: action,
      setShowCreateNewSkillModalHandler: action,
      setCurrentProject: action,
      toggleSkillHandler: action,
      showModalHandler: action,
      setShowProjectTable: action,
      showEditSkillModalHandler: action,
      showNewSkillModalHandler: action,
      setSelectedSkillLevel: action,
      setIsSearching: action,
      removeLinkedSkillProject: action,
      addLinkedProjectToSkill: action,
      getRecentSkills: action,
      getAllSkills: action,
      postNewSkillToUser: action,
      postNewSkillProject: action,
      deleteNewSkillProject: action,
      deleteNewSkillProjectLinks: action,
      getUserDetailedProjects: action,
      getUserDetailedSkillLinkedProjects: action,
      editUserSkill: action,
      createNewSkill: action,
      deleteUserSkill: action,
      addInterest: action,
      removeInterest: action,
      setShowEditInfo: action,
      editSkillInfo: action,
      addSkillTag: action,
      removeSkillTag: action,
      selectEditSkill: action,
      getTags: action,
      getCategories: action,
      getDepSkills: action,
      getAllDepSkills: action,
      getUserDep: action,
    })
  }

  /**
   * * Actions
   */

  get dropdownTags() {
    return this.tags.map((x) => ({
      key: x.id,
      value: x.name,
      text: x.name,
    }))
  }

  get filteredSkills() {
    const searchParam = this.searchValue.toLowerCase().trim()

    // First filter by category if a category filter is set
    let filteredByCategory = this.allSkills
    if (this.categoryFilter !== null) {
      filteredByCategory = filteredByCategory.filter((skill) => skill.skill.skillCategoryId === this.categoryFilter)
    }

    // Then filter by parent if a parent filter is set
    if (this.parentFilter !== null) {
      filteredByCategory = filteredByCategory.filter((skill) => {
        const s = skill.skill
        return s.parentSkillId === this.parentFilter || (s.isParentSkill && s.skillId === this.parentFilter)
      })
    }

    // If no search parameter, just sort the category-filtered list
    if (!searchParam) {
      const sortedList = sortBy(filteredByCategory, (skill) => sortByType(skill.skill, this.sortColumn))
      return this.sortDirection === 'descending' ? sortedList.reverse() : sortedList
    }

    // For search, add relevance scoring on the category-filtered list
    const scoredResults = filteredByCategory
      .map((skill: ISkills) => {
        const { skillNameFi, skillNameEng, skillDescription, categoryName, skillTags } = skill.skill

        // Normalize values to prevent null/undefined errors
        const name = (getI18n().language === 'en' && skillNameEng?.length ? skillNameEng : skillNameFi)?.toLowerCase() || ''
        const description = skillDescription?.toLowerCase() || ''
        const category = categoryName?.toLowerCase() || ''
        const tags = skillTags?.toLowerCase() || ''

        // Calculate relevance score - higher is better
        let score = 0

        // Exact match in name is highest priority
        if (name === searchParam) score += 100
        // Name starting with search term is high priority
        else if (name.startsWith(searchParam)) score += 75
        // Name containing search term is medium-high priority
        else if (name.includes(searchParam)) score += 50

        // Tags are next most important for relevancy
        if (tags.includes(searchParam)) score += 40

        // Category matches
        if (category.includes(searchParam)) score += 30

        // Description matches are least relevant but still count
        if (description.includes(searchParam)) score += 20

        // If no matches in any field, score remains 0

        return { skill, score }
      })
      .filter((item) => item.score > 0)

    // Sort by score first (descending), then by the selected column if scores are equal
    const sortedResults = sortBy(scoredResults, [
      // Primary sort by relevance score (descending)
      (item) => -item.score,
      // Secondary sort by selected column
      (item) => sortByType(item.skill.skill, this.sortColumn),
    ])

    // Extract just the skills from the scored results
    const filteredList = sortedResults.map((item) => item.skill)

    // Apply sort direction only to column sort, not to relevance
    return this.sortDirection === 'descending' && this.searchValue === '' ? filteredList.reverse() : filteredList
  }

  get dropdownCategories() {
    return this.categories.map((x) => ({
      key: x.id,
      value: x.id,
      text: x.name,
    }))
  }

  get dropdownSkills() {
    return this.allSkills
      .filter((skill) => skill.userHas)
      .map((skill) => ({
        key: skill.skill.skillId,
        value: skill.skill.skillId,
        text: getI18n().language === 'en' && skill.skill.skillNameEng?.length ? skill.skill.skillNameEng : skill.skill.skillNameFi,
      }))
      .sort((a, b) => a.text.localeCompare(b.text))
  }

  get allParentSkills() {
    return this.allSkills
      .filter((skill) => skill.skill.isParentSkill === true)
      .map((skill) => ({
        key: skill.skill.skillId,
        value: skill.skill.skillId,
        text: getI18n().language === 'en' && skill.skill.skillNameEng?.length ? skill.skill.skillNameEng : skill.skill.skillNameFi,
      }))
      .sort((a, b) => a.text.localeCompare(b.text))
  }

  get allDropdownSkills() {
    return this.allSkills
      .map((skill) => ({
        key: skill.skill.skillId,
        value: skill.skill.skillId,
        text: getI18n().language === 'en' && skill.skill.skillNameEng?.length ? skill.skill.skillNameEng : skill.skill.skillNameFi,
      }))
      .sort((a, b) => a.text.localeCompare(b.text))
  }

  getFilteredSkillsByText = (text: string) => {
    return this.filteredSkills.filter(
      (skill: ISkills) =>
        (getI18n().language === 'en' && skill.skill.skillNameEng?.length ? skill.skill.skillNameEng : skill.skill.skillNameFi)
          ?.toLowerCase()
          .includes(text.toLowerCase()) ||
        skill.skill.skillDescription?.toLowerCase().includes(text.toLowerCase()) ||
        skill.skill.categoryName?.toLowerCase().includes(text.toLowerCase()) ||
        skill.skill.skillTags?.toLowerCase().includes(text.toLowerCase()),
    )
  }

  setShowAddProjectToSkillModal = (value: boolean) => {
    this.showAddProjectToSkillModal = value
  }

  setCurrentskill = (value: any) => {
    this.currentSkill = value
  }

  setSaving = (value: boolean) => {
    this.saving = value
  }

  setDeleting = (value: boolean) => {
    this.deleting = value
  }

  setSearchValue = (value: string) => {
    this.searchValue = value
  }

  setCategoryFilter = (categoryId: number | null) => {
    this.categoryFilter = categoryId
  }

  setParentFilter = (parentId: number | null) => {
    this.parentFilter = parentId
  }

  setSortColumn = (value: string) => {
    this.sortColumn = value
  }

  toggleSortDirection = () => {
    this.sortDirection = this.sortDirection === 'ascending' ? 'descending' : 'ascending'
  }

  setAllSkills = (skills: Array<ISkills>) => {
    this.allSkills = skills
  }

  clearEditCurrent = () => {
    this.editCurrentProject = []
    this.editCurrentSkill = {
      approved: false,
      comment: '',
      experience: 0,
      interest: 0,
      linkedProjects: [],
      skillId: 0,
      skillLevel: 0,
      skillNameFi: '',
      skillNameEng: '',
      skillDescription: '',
      skillDescriptionEng: '',
      parentSkillId: 0,
    }
  }

  setEditCurrentProject = (value: any) => {
    this.editCurrentProject = value
  }

  setShowCreateNewSkillModalHandler = (show: boolean) => {
    this.showCreateNewSkillModal = show
  }

  setCurrentProject = (cProject: Record<string, unknown>) => {
    this.currentProject = cProject
  }

  toggleSkillHandler = () => {
    this.showSkill = !this.showSkill
  }

  showModalHandler = (val = !this.showModal) => {
    this.showModal = val
  }

  setShowProjectTable = (show: boolean) => {
    this.showProjectTable = show
  }

  showEditSkillModalHandler = (value?: boolean | undefined) => {
    if (value !== undefined) {
      this.showEditSkillModal = value
    } else {
      this.showEditSkillModal = false
    }
  }

  showNewSkillModalHandler = (val = !this.showNewSkillModal) => {
    this.showNewSkillModal = val
    this.selectedSkillLevel = null
  }

  setSelectedSkillLevel = (skillLevel: number | null) => {
    this.selectedSkillLevel = skillLevel
  }

  setIsSearching = (isItSearching: boolean) => {
    this.isSearching = isItSearching
  }

  removeLinkedSkillProject = (projectId: number) => {
    if (this.editCurrentProject != null) {
      this.editCurrentProject = this.editCurrentProject.filter((linkedProject: any) => linkedProject.projectId !== projectId)
    }
  }

  addLinkedProjectToSkill = (linkedProject: any) => {
    const foundSkill = this.editCurrentProject.find((pro) => pro.projectId === linkedProject.projectId)
    if (foundSkill === undefined) {
      this.editCurrentProject = [...this.editCurrentProject, linkedProject]
    }
  }

  getRecentSkills = async (userId: number) => {
    this.loadingRecentSkills = true
    try {
      const fetchedSkills: ISkills[] = await agent.HomePage.getRecentSkills(userId)
      runInAction(() => {
        this.recentSkills = fetchedSkills
        this.loadingRecentSkills = false
      })
    } catch (error) {
      runInAction(() => {
        this.loadingRecentSkills = false
      })
    }
  }

  getAllSkills = async (userId: number, useLoading = true) => {
    if (useLoading) {
      this.loadingSkills = true
    }
    try {
      const fetchedSkills: ISkills[] = await agent.Skills.getAllSkills(userId)
      runInAction(() => {
        this.allSkills = fetchedSkills
        if (useLoading) {
          this.loadingSkills = false
        }
      })
    } catch (error) {
      runInAction(() => {
        if (useLoading) {
          this.loadingSkills = false
        }
      })
    }
  }

  postNewSkillToUser = async (newSkill: INewSkill, userId: number, update = true) => {
    try {
      await agent.Skills.postNewSkillToUser(newSkill)
      runInAction(async () => {
        if (update) await this.getAllSkills(userId)
      })
    } catch (error) {
      return Promise.reject(error)
    }
    return Promise.resolve()
  }

  postNewSkillProject = async (newSkillProject: any) => {
    try {
      await agent.Skills.postPersonSkillProject(newSkillProject)
    } catch (error) {
      return Promise.reject(error)
    }
    return Promise.resolve()
  }
  postNewSkillProjectLinks = async (newSkillLinkProjects: ISkillProject[]) => {
    try {
      await agent.Skills.postPersonLinkedSkills(newSkillLinkProjects)
    } catch (error) {
      return Promise.reject(error)
    }
    return Promise.resolve()
  }

  deleteNewSkillProject = async (userId: number, skillId: number, projectId: number) => {
    try {
      await agent.Skills.deletePersonSkillProject({
        userId,
        skillId,
        projectId,
      })
    } catch (error) {
      return Promise.reject(error)
    }
    return Promise.resolve()
  }

  deleteNewSkillProjectLinks = async (linksToDelete: ISkillProject[]) => {
    try {
      await agent.Skills.deleteLinkedSkills(linksToDelete)
    } catch (error) {
      return Promise.reject(error)
    }
    return Promise.resolve()
  }

  getUserDetailedProjects = async (userId: number) => {
    const fetchedProjects: IProjects[] = await agent.Skills.linkProjectToSkill(userId)
    runInAction(() => {
      this.allUserProjectIds = fetchedProjects
    })
  }

  getUserDetailedSkillLinkedProjects = async (userId: number, skillId: number) => {
    this.loadingInitial = true
    try {
      const detailed = await agent.Skills.getUserLinkedSkillProjects(userId, skillId)
      runInAction(() => {
        this.editCurrentSkill = detailed
        this.editCurrentProject = detailed.linkedProjects
        this.loadingInitial = false
        this.isNewPersonSkill = false
      })
    } catch (error) {
      runInAction(() => {
        this.editCurrentSkill = {
          approved: false,
          comment: '',
          experience: 0,
          interest: 0,
          linkedProjects: [],
          skillId,
          skillLevel: 0,
          skillNameFi: '',
          skillNameEng: null,
          skillDescription: '',
          skillDescriptionEng: '',
          parentSkillId: 0,
        }
        this.loadingInitial = false
        this.isNewPersonSkill = true
      })
    }
    const skill = this.editCurrentSkill
    const isNew = this.isNewPersonSkill
    return Promise.resolve({ skill, isNew })
  }

  editUserSkill = async (editedSkill: INewSkill, userId: number, getAll = true) => {
    try {
      await agent.Skills.editUserSkill(editedSkill)
      runInAction(async () => {
        if (getAll) await this.getAllSkills(userId)
      })
    } catch (error) {
      return Promise.reject(error)
    }
    return Promise.resolve()
  }

  createNewSkill = async (newSkill: any, addSkillToUser: INewSkill, addToUser: boolean) => {
    try {
      const newSkillId = await agent.Skills.createNewSkill(newSkill)
      addSkillToUser.skillId = newSkillId
      if (addToUser) {
        await agent.Skills.postNewSkillToUser(addSkillToUser)
      }
      const updatedSkills = await agent.Skills.getAllSkills(newSkill.creatorId)
      runInAction(() => {
        this.allSkills = updatedSkills
      })
    } catch (error) {
      return Promise.reject(error)
    }
    return Promise.resolve()
  }

  deleteUserSkill = async (userId: number, skillId: number, update = true) => {
    try {
      await agent.Skills.deleteUserSkill(userId, skillId)
      if (update) {
        const updatedRecentSkills = await agent.HomePage.getRecentSkills(userId)
        const updatedSkills = await agent.Skills.getAllSkills(userId)
        runInAction(() => {
          this.recentSkills = updatedRecentSkills
          this.allSkills = updatedSkills
        })
      }
    } catch (error) {
      return Promise.reject(error)
    }
    return Promise.resolve()
  }

  addInterest = async (userId: number, skillId: number) => {
    this.loadingLikeButton = true
    try {
      await agent.Skills.addInterest(userId, skillId)
      runInAction(async () => {
        const foundSkill = this.allSkills.find((skill) => skill.skill.skillId === skillId)
        if (foundSkill) {
          foundSkill.userInterested = true
        }
        this.loadingLikeButton = false
      })
    } catch (error) {
      runInAction(() => {
        this.loadingLikeButton = false
      })
      return Promise.reject(error)
    }
    return Promise.resolve()
  }
  removeInterest = async (userId: number, skillId: number) => {
    this.loadingLikeButton = true
    try {
      await agent.Skills.removeInterest(userId, skillId)
      runInAction(async () => {
        const foundSkill = this.allSkills.find((skill) => skill.skill.skillId === skillId)
        if (foundSkill) {
          foundSkill.userInterested = false
        }
        this.loadingLikeButton = false
      })
    } catch (error) {
      runInAction(() => {
        this.loadingLikeButton = false
      })
      return Promise.reject(error)
    }
    return Promise.resolve()
  }

  setShowEditInfo = (value: boolean) => {
    this.showEditInfo = value
    this.showUserSkillEdit = !value
  }

  editSkillInfo = async (userId: number, skillId: number, body: IEditedSkillInfo, update = true) => {
    try {
      await agent.Skills.editSkill(skillId, body)
      runInAction(async () => {
        if (update) await this.getAllSkills(userId)
      })
    } catch (error) {
      return Promise.reject(error)
    }
    return Promise.resolve()
  }

  addSkillTag = async (tagId: number, skillId: number) => {
    await agent.Skills.addSkillTag(tagId, skillId)
  }

  removeSkillTag = async (tagId: number, skillId: number) => {
    await agent.Skills.removeSkillTag(tagId, skillId)
  }

  selectEditSkill = (skill: ISkill) => {
    this.selectedEditSkill = { ...skill }
  }

  getTags = async () => {
    this.loadingTags = true
    const resp: Array<ICategoryTag> = await agent.Skills.getTags()
    runInAction(() => {
      this.tags = sortBy(resp, (tag) => tag.name?.toLowerCase())
      this.loadingTags = false
    })
  }

  getCategories = async () => {
    this.loadingCategories = true
    const resp: Array<ICategoryTag> = await agent.Skills.getCats()
    runInAction(() => {
      this.categories = sortBy(resp, (category) => category.name?.toLowerCase())
      this.loadingCategories = false
    })
  }

  getDepSkills = async (userId: number) => {
    this.loadingNeededSkills = true
    try {
      const fetchedNeededSkills: ISkillDepObject[] = await agent.Skills.getDepartmentSkills(userId)
      const fetchedUserDep = await agent.Persons.getUserDep(userId)
      runInAction(() => {
        this.userNeededSkills = fetchedNeededSkills
        this.userDepartment = fetchedUserDep
        this.loadingNeededSkills = false
      })
    } catch (error) {
      return Promise.reject(error)
    }
    return Promise.resolve()
  }

  getAllDepSkills = async (userId: number) => {
    this.loadingAllNeededSkills = true
    try {
      this.sortedNeededSkills = []
      const fetchedAllDepsAndSkills = await agent.Skills.getAllDepartmentSkills(userId)

      runInAction(() => {
        this.sortedNeededSkills = fetchedAllDepsAndSkills
        this.loadingAllNeededSkills = false
      })
    } catch (error) {
      return Promise.reject(error)
    }
    return Promise.resolve()
  }

  getUserDep = async (userId: number) => {
    try {
      this.userDepartment = ''
      const fetchedUserDep = await agent.Persons.getUserDep(userId)

      runInAction(() => {
        this.userDepartment = fetchedUserDep
      })
    } catch (error) {
      return Promise.reject(error)
    }
    return Promise.resolve()
  }
}
